import { FC, useCallback, useMemo, useState } from 'react';
import { useHistory, useParams, generatePath } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Box } from '@rebass/emotion';

import ErrorBoundary from '@app/components/ErrorBoundary';
import PageTitle from '@app/components/PageTitle';
import Loader from '@app/components/Loader';
import Link from '@app/components/Link';
import useCart from '@app/shop/hooks/useCart';
import { AlertItem } from '@app/shop/hooks/useAlerts';
import Alerts from '@app/shop/components/Alerts';
import { ShopStoreProvider } from '@app/shop/context/ShopStoreContext';
import { CartContextValue, CartProvider } from '@app/shop/context/CartContext';
import {
  AlertAction,
  AlertLayout,
  AlertMessage,
  AlertTitle,
  AlertType,
} from '@app/shop/components/Alert';
import { Button } from '@app/shop/components/Button';
import { CartQuery_magento2_cart_items } from '@app/shop/context/__generated__/CartQuery';
import { Magento2_ProductStockStatus } from '@__generated__/globalTypes';
import useGTMTracking from '@app/shop/hooks/useGTMTracking';

import CartItems from './CartItems/CartItems';
import CartTotals from './CartTotals/CartTotals';
import ShopStoreAlerts from './ShopStoreAlerts/ShopStoreAlerts';
import CartAlerts from './CartAlerts';
import CartCoupon from './CartCoupon';
import CartEmpty from './CartEmpty';
import { CONTINUE_SHOPPING_LINK, CHECKOUT_LINK } from './Cart.constants';
import messages from './Cart.messages';
import {
  CartGrid,
  CartTotalsCell,
  CheckoutButtonCell,
  ContinueShoppingButtonCell,
} from './Cart.styles';

const CartContent: FC = () => {
  const { push } = useHistory();
  const { language } = useParams<{ language: string }>();
  const { loading, items, prices, storeConfig, cartId, removeItem } = useCart();
  const [checkoutAttempts, setCheckoutAttempts] = useState(0);
  const outOfStockAlerts = useMemo(
    () =>
      items
        ?.filter(isOutOfStockItem)
        .map(itemToOutOfStockAlert(removeItem, checkoutAttempts > 0)) ?? [],
    [items, removeItem, checkoutAttempts]
  );

  useGTMTracking({ step: 'cart', items, prices, storeConfig, cartId });

  const handleCheckoutClick = useCallback(() => {
    if (outOfStockAlerts?.length) {
      setCheckoutAttempts((current) => current + 1);

      return;
    }

    push(generatePath(CHECKOUT_LINK, { language }));
  }, [outOfStockAlerts, push, language]);

  if (loading) {
    return <Loader />;
  }

  if (!items?.length) {
    return <CartEmpty />;
  }

  return (
    <>
      <Box mb={16}>
        <ShopStoreAlerts />
      </Box>

      <Box mb={16}>
        <CartAlerts />
      </Box>

      {!!outOfStockAlerts.length && (
        <Box mb={16}>
          {/* change key to force re-render, triggering shake animation if set */}
          <Alerts alerts={outOfStockAlerts} key={checkoutAttempts} />
        </Box>
      )}

      <CartGrid>
        <CartItems />
        <CartCoupon />

        <ContinueShoppingButtonCell hAlign="left">
          <Link
            to={CONTINUE_SHOPPING_LINK}
            data-testid="cart-continue-shopping-button"
          >
            <Button type="secondary">
              <FormattedMessage {...messages.continueShoppingCTA} />
            </Button>
          </Link>
        </ContinueShoppingButtonCell>

        <CartTotalsCell hAlign="right">
          <CartTotals />
        </CartTotalsCell>

        <CheckoutButtonCell hAlign="right">
          <Button
            type="primary"
            onClick={handleCheckoutClick}
            data-testid="cart-checkout-button"
          >
            <FormattedMessage {...messages.checkoutCTA} />
          </Button>
        </CheckoutButtonCell>
      </CartGrid>
    </>
  );
};

const Cart: FC = () => (
  <>
    <PageTitle>{messages.title}</PageTitle>
    <Box p={40} bg="white" flex={1}>
      <ErrorBoundary>
        <ShopStoreProvider>
          <CartProvider>
            <CartContent />
          </CartProvider>
        </ShopStoreProvider>
      </ErrorBoundary>
    </Box>
  </>
);

export default Cart;

const isOutOfStockItem = (
  item: CartQuery_magento2_cart_items | null
): item is CartQuery_magento2_cart_items =>
  !!(item?.product.stockStatus === Magento2_ProductStockStatus.OUT_OF_STOCK);

const itemToOutOfStockAlert = (
  removeItem: CartContextValue['removeItem'],
  shouldShake: boolean
) => ({ uid, product }: CartQuery_magento2_cart_items): AlertItem => ({
  shouldShake,
  type: AlertType.Error,
  layout: AlertLayout.Column,
  content: (
    <>
      <AlertTitle>
        <FormattedMessage
          {...messages.alertOutOfStockTitle}
          values={{ productName: product.name }}
        />
      </AlertTitle>
      <AlertMessage>
        <FormattedMessage {...messages.alertOutOfStockMessage} />
      </AlertMessage>
    </>
  ),
  actions: (
    <AlertAction>
      <button
        onClick={() => removeItem(uid)}
        data-testid={`alert-out-of-stock-remove-${product.sku}`}
      >
        <FormattedMessage {...messages.alertOutOfStockActionRemove} />
      </button>
    </AlertAction>
  ),
});
